import { callFunctionRaw, resolveFunctionAccessToken } from "@/lib/callFunction";
import { resolveValidatedSession } from "@/lib/authSession.js";

export function isWorldlineSandboxEntryEnabled() {
  return (
    String(import.meta.env.VITE_APP_ENV || "").trim().toLowerCase() === "staging" &&
    String(import.meta.env.VITE_WORLDLINE_SANDBOX_ENABLED || "").trim().toLowerCase() === "true"
  );
}

async function accessToken() {
  let token = await resolveFunctionAccessToken({ refreshIfMissing: true, forceRefresh: false });
  if (!token) token = String((await resolveValidatedSession())?.access_token || "").trim();
  if (!token) throw new Error("Session expired. Please sign in again.");
  return token;
}

export async function createWorldlineHostedCheckout({ clinicianUserId, serviceId, startsAt, idempotencyKey, locale }) {
  return callFunctionRaw("worldline-create-checkout", {
    clinician_user_id: String(clinicianUserId || "").trim(),
    service_id: String(serviceId || "").trim(),
    starts_at: String(startsAt || "").trim(),
    idempotency_key: String(idempotencyKey || "").trim(),
    locale: String(locale || "").trim().toLowerCase() === "fr" ? "fr" : "en",
  }, { accessToken: await accessToken(), timeoutMs: 15000, preferFreshSession: false });
}

export async function getWorldlineSandboxAvailability() {
  try {
    const result = await callFunctionRaw("worldline-sandbox-availability", {}, { accessToken: await accessToken(), timeoutMs: 8000, preferFreshSession: false });
    return Boolean(result?.eligible);
  } catch {
    return false;
  }
}

export async function verifyWorldlineHostedAttempt(attemptId) {
  return callFunctionRaw("worldline-verify-attempt", { attempt_id: String(attemptId || "").trim() }, { accessToken: await accessToken(), timeoutMs: 15000, preferFreshSession: true });
}

export async function createWorldlineSponsorLink({ clinicianUserId, serviceId, startsAt }) {
  return callFunctionRaw("worldline-create-sponsor-link", {
    action: "create",
    clinician_user_id: String(clinicianUserId || "").trim(),
    service_id: String(serviceId || "").trim(),
    starts_at: String(startsAt || "").trim(),
  }, { accessToken: await accessToken(), timeoutMs: 15000, preferFreshSession: false });
}

export async function revokeWorldlineSponsorLink(sponsorLinkId) {
  return callFunctionRaw("worldline-create-sponsor-link", {
    action: "revoke",
    sponsor_link_id: String(sponsorLinkId || "").trim(),
  }, { accessToken: await accessToken(), timeoutMs: 15000, preferFreshSession: false });
}

export async function resolveWorldlineSponsorLink(token) {
  return callFunctionRaw("worldline-sponsor-link", {
    action: "resolve",
    token: String(token || "").trim(),
  }, { skipAuth: true, timeoutMs: 12000 });
}

export async function startWorldlineSponsorCheckout({ token, locale }) {
  return callFunctionRaw("worldline-sponsor-link", {
    action: "start_checkout",
    token: String(token || "").trim(),
    locale: String(locale || "").trim().toLowerCase() === "fr" ? "fr" : "en",
  }, { skipAuth: true, timeoutMs: 15000 });
}

export async function verifyWorldlineSponsorCheckout(token) {
  return callFunctionRaw("worldline-sponsor-link", {
    action: "verify",
    token: String(token || "").trim(),
  }, { skipAuth: true, timeoutMs: 15000 });
}
